import type { UnapprovedAllocationDto } from '@billda/api';

export type AllocationTypeFilter = 'all' | 'variations' | 'labour';

export interface UnapprovedPotGroup {
  potId: string;
  /** Claimant (gang or worker) — the pot's identity. A pot can span sites, so
   * this, not a single site name, labels the group. */
  claimantName: string;
  /** True when the claimant is a gang (vs a solo worker) — drives the row icon. */
  isGang: boolean;
  potStatus: UnapprovedAllocationDto['potStatus'];
  unapprovedCount: number;
  subtotal: number;
  allocations: UnapprovedAllocationDto[];
}

export interface UnapprovedWeekGroup {
  periodYear: number;
  periodWeekNumber: number;
  weekCommencing: string;
  pots: UnapprovedPotGroup[];
}

/** Splits allocations by type: all, variations only, or labour items only. */
export function filterByType(
  allocations: UnapprovedAllocationDto[],
  filter: AllocationTypeFilter
): UnapprovedAllocationDto[] {
  if (filter === 'variations') {
    return allocations.filter(a => a.isVariation);
  }
  if (filter === 'labour') {
    return allocations.filter(a => !a.isVariation);
  }
  return allocations;
}

/**
 * Groups allocations by their parent pot's period (oldest week first), then
 * by parent pot within each week, computing each pot's unapproved count and
 * £ subtotal along the way. Oldest first surfaces the work that has waited
 * longest for approval.
 */
export function groupByWeekThenPot(
  allocations: UnapprovedAllocationDto[]
): UnapprovedWeekGroup[] {
  const weeksByKey = new Map<string, UnapprovedWeekGroup>();

  for (const allocation of allocations) {
    const week = getOrCreateWeek(weeksByKey, allocation);
    const pot = getOrCreatePot(week, allocation);
    pot.allocations.push(allocation);
    pot.unapprovedCount += 1;
    pot.subtotal += allocation.amountClaimed;
  }

  return Array.from(weeksByKey.values()).sort(
    (a, b) =>
      a.periodYear - b.periodYear || a.periodWeekNumber - b.periodWeekNumber
  );
}

function getOrCreateWeek(
  weeksByKey: Map<string, UnapprovedWeekGroup>,
  allocation: UnapprovedAllocationDto
): UnapprovedWeekGroup {
  const key = `${allocation.periodYear}-${allocation.periodWeekNumber}`;
  const existing = weeksByKey.get(key);
  if (existing) {
    return existing;
  }

  const week: UnapprovedWeekGroup = {
    periodYear: allocation.periodYear,
    periodWeekNumber: allocation.periodWeekNumber,
    weekCommencing: allocation.weekCommencing,
    pots: [],
  };
  weeksByKey.set(key, week);
  return week;
}

function getOrCreatePot(
  week: UnapprovedWeekGroup,
  allocation: UnapprovedAllocationDto
): UnapprovedPotGroup {
  const existing = week.pots.find(p => p.potId === allocation.potId);
  if (existing) {
    return existing;
  }

  const pot: UnapprovedPotGroup = {
    potId: allocation.potId,
    claimantName: allocation.claimantName,
    isGang: allocation.isGang,
    potStatus: allocation.potStatus,
    unapprovedCount: 0,
    subtotal: 0,
    allocations: [],
  };
  week.pots.push(pot);
  return pot;
}
